import { put, call, takeLeading } from "redux-saga/effects";
import request from "utils/api";
import { safe } from "providers/GeneralProvider/saga";
import {
  getOrderOverviewRequest,
  getOrderOverviewSuccess,
  getRevenueByTimeRequest,
  getRevenueByTimeSuccess,
  getOrderStatusDistributionRequest,
  getOrderStatusDistributionSuccess,
  getTopProductsRequest,
  getTopProductsSuccess,
  getOrdersByPaymentMethodRequest,
  getOrdersByPaymentMethodSuccess,
  getOrdersBySourceRequest,
  getOrdersBySourceSuccess,
  getUserGrowthRequest,
  getUserGrowthSuccess,
  getUserStatusDistributionRequest,
  getUserStatusDistributionSuccess,
  getUserAuthMethodsRequest,
  getUserAuthMethodsSuccess,
  getTopUsersByFollowersRequest,
  getTopUsersByFollowersSuccess,
  getUserEngagementRequest,
  getUserEngagementSuccess,
  getPostTrendsRequest,
  getPostTrendsSuccess,
  getReactionsSummaryRequest,
  getReactionsSummarySuccess,
  getTopPostsRequest,
  getTopPostsSuccess,
  getContentTypeBreakdownRequest,
  getContentTypeBreakdownSuccess,
  getChatStatsRequest,
  getChatStatsSuccess,
  getPostDetailRequest,
  getPostDetailSuccess,
  stopLoading,
} from "providers/DashboardStatsProvider/slice";

const BASE = "api/admin/dashboard-stats";

// ========== ORDER STATS ==========
function* getOrderOverview(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/orders/overview`, { startDate, endDate }, { method: "GET" });
  yield put(getOrderOverviewSuccess(response.data));
}

function* getRevenueByTime(action) {
  const { startDate, endDate, granularity } = action.payload;
  const response = yield call(request, `${BASE}/orders/revenue-by-time`, { startDate, endDate, granularity }, { method: "GET" });
  yield put(getRevenueByTimeSuccess(response.data));
}

function* getOrderStatusDistribution(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/orders/status-distribution`, { startDate, endDate }, { method: "GET" });
  yield put(getOrderStatusDistributionSuccess(response.data));
}

function* getTopProducts(action) {
  const { startDate, endDate, limit } = action.payload;
  const response = yield call(request, `${BASE}/orders/top-products`, { startDate, endDate, limit }, { method: "GET" });
  yield put(getTopProductsSuccess(response.data));
}

function* getOrdersByPaymentMethod(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/orders/by-payment-method`, { startDate, endDate }, { method: "GET" });
  yield put(getOrdersByPaymentMethodSuccess(response.data));
}

function* getOrdersBySource(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/orders/by-source`, { startDate, endDate }, { method: "GET" });
  yield put(getOrdersBySourceSuccess(response.data));
}

// ========== USER STATS ==========
function* getUserGrowth(action) {
  const { startDate, endDate, granularity } = action.payload;
  const response = yield call(request, `${BASE}/users/growth`, { startDate, endDate, granularity }, { method: "GET" });
  yield put(getUserGrowthSuccess(response.data));
}

function* getUserStatusDistribution() {
  const response = yield call(request, `${BASE}/users/status-distribution`, {}, { method: "GET" });
  yield put(getUserStatusDistributionSuccess(response.data));
}

function* getUserAuthMethods() {
  const response = yield call(request, `${BASE}/users/auth-methods`, {}, { method: "GET" });
  yield put(getUserAuthMethodsSuccess(response.data));
}

function* getTopUsersByFollowers(action) {
  const { limit } = action.payload || {};
  const response = yield call(request, `${BASE}/users/top-by-followers`, { limit: limit || 10 }, { method: "GET" });
  yield put(getTopUsersByFollowersSuccess(response.data));
}

function* getUserEngagement(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/users/engagement`, { startDate, endDate }, { method: "GET" });
  yield put(getUserEngagementSuccess(response.data));
}

// ========== SOCIAL STATS ==========
function* getPostTrends(action) {
  const { startDate, endDate, granularity } = action.payload;
  const response = yield call(request, `${BASE}/social/post-trends`, { startDate, endDate, granularity }, { method: "GET" });
  yield put(getPostTrendsSuccess(response.data));
}

function* getReactionsSummary(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/social/reactions-summary`, { startDate, endDate }, { method: "GET" });
  yield put(getReactionsSummarySuccess(response.data));
}

function* getTopPosts(action) {
  const { startDate, endDate, limit } = action.payload;
  const response = yield call(request, `${BASE}/social/top-posts`, { startDate, endDate, limit }, { method: "GET" });
  yield put(getTopPostsSuccess(response.data));
}

function* getContentTypeBreakdown(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/social/content-type-breakdown`, { startDate, endDate }, { method: "GET" });
  yield put(getContentTypeBreakdownSuccess(response.data));
}

function* getChatStats(action) {
  const { startDate, endDate } = action.payload;
  const response = yield call(request, `${BASE}/social/chat-stats`, { startDate, endDate }, { method: "GET" });
  yield put(getChatStatsSuccess(response.data));
}

function* getPostDetail(action) {
  const { id } = action.payload;
  const response = yield call(request, `${BASE}/social/post-detail/${id}`, {}, { method: "GET" });
  yield put(getPostDetailSuccess(response.data));
}

function* onError() {
  yield put(stopLoading());
}

export default function* watchDashboardStats() {
  yield takeLeading(getOrderOverviewRequest.type, safe(getOrderOverview, onError));
  yield takeLeading(getRevenueByTimeRequest.type, safe(getRevenueByTime, onError));
  yield takeLeading(getOrderStatusDistributionRequest.type, safe(getOrderStatusDistribution, onError));
  yield takeLeading(getTopProductsRequest.type, safe(getTopProducts, onError));
  yield takeLeading(getOrdersByPaymentMethodRequest.type, safe(getOrdersByPaymentMethod, onError));
  yield takeLeading(getOrdersBySourceRequest.type, safe(getOrdersBySource, onError));
  yield takeLeading(getUserGrowthRequest.type, safe(getUserGrowth, onError));
  yield takeLeading(getUserStatusDistributionRequest.type, safe(getUserStatusDistribution, onError));
  yield takeLeading(getUserAuthMethodsRequest.type, safe(getUserAuthMethods, onError));
  yield takeLeading(getTopUsersByFollowersRequest.type, safe(getTopUsersByFollowers, onError));
  yield takeLeading(getUserEngagementRequest.type, safe(getUserEngagement, onError));
  yield takeLeading(getPostTrendsRequest.type, safe(getPostTrends, onError));
  yield takeLeading(getReactionsSummaryRequest.type, safe(getReactionsSummary, onError));
  yield takeLeading(getTopPostsRequest.type, safe(getTopPosts, onError));
  yield takeLeading(getContentTypeBreakdownRequest.type, safe(getContentTypeBreakdown, onError));
  yield takeLeading(getChatStatsRequest.type, safe(getChatStats, onError));
  yield takeLeading(getPostDetailRequest.type, safe(getPostDetail, onError));
}
